import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { Add, LockOutlined } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../lib/api';
import opportunityCreateFields from '../../lib/config/opportunityCreateFields';

const CreateContactOpportunity = ({ contact }) => {
    const [customer, setCustomer] = useState(null);
    const [anchor, setAnchor] = useState(null);
    const [values, setValues] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const submitting = useRef(false);
    const { user } = useAuth();
    const navigate = useNavigate();
    const customers = contact.customers || [];
    const contactName = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.name || contact.primaryEmail;
    const owner = user?.userId || user?.id || localStorage.getItem('id');
    const openModal = selected => {
        setCustomer(selected);
        setAnchor(null);
        setValues({});
        setError('');
    };
    const submit = async event => {
        event.preventDefault();
        if (submitting.current) return;
        if (!owner || !customer?._id || !contact._id) {
            setError('A signed-in user, customer, and contact are required.');
            return;
        }
        if (opportunityCreateFields.some(field => field.required && !values[field.name]?.trim())) {
            setError('Complete all required opportunity fields.');
            return;
        }
        submitting.current = true;
        setSaving(true);
        setError('');
        try {
            const email = user?.email || localStorage.getItem('email');
            const response = await API.Opportunities.Create({
                ...Object.fromEntries(opportunityCreateFields
                    .filter(field => values[field.name]?.trim())
                    .map(field => [field.name, values[field.name].trim()])),
                owner,
                customer: customer._id,
                primaryContact: contact._id,
                createdBy: email,
                lastUpdatedBy: email,
                lastUpdatedDate: Date.now(),
                salesStage: '01 - Lead',
            });
            if (response.status !== 200 && response.status !== 201) {
                throw new Error(typeof response.data?.message === 'string' ? response.data.message : 'Unable to create opportunity. Please try again.');
            }
            const opportunityId = response.data?.opportunity?._id || response.data?._id;
            setCustomer(null);
            navigate(opportunityId ? `/opportunities/${opportunityId}` : '/opportunities');
        } catch (err) {
            setError(err.message || 'Unable to create opportunity. Please try again.');
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    return <>
        <Tooltip title={!customers.length ? 'Link this contact to a customer before creating an opportunity.' : ''}>
            <span><Button variant="contained" startIcon={<Add />} disabled={!customers.length || !owner}
                onClick={event => customers.length === 1 ? openModal(customers[0]) : setAnchor(event.currentTarget)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Create Opportunity</Button></span>
        </Tooltip>
        <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>Create opportunity for customer</Typography>
            {customers.map(item => <MenuItem key={item._id} onClick={() => openModal(item)}>{item.organizationName}</MenuItem>)}
        </Menu>
        <Dialog open={Boolean(customer)} onClose={() => { if (!saving) setCustomer(null); }} fullWidth maxWidth="sm" aria-labelledby="create-contact-opportunity-title">
            <Box component="form" onSubmit={submit}>
                <DialogTitle id="create-contact-opportunity-title">Create Opportunity</DialogTitle>
                <DialogContent dividers>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Stack spacing={2} sx={{ pt: 1 }}>
                        <TextField label="Customer" value={customer?.organizationName || ''} disabled
                            InputProps={{ endAdornment: <InputAdornment position="end"><LockOutlined fontSize="small" /></InputAdornment> }} />
                        <TextField label="Primary Contact" value={contactName || ''} disabled
                            InputProps={{ endAdornment: <InputAdornment position="end"><LockOutlined fontSize="small" /></InputAdornment> }} />
                        {opportunityCreateFields.map(field => field.options
                            ? <Autocomplete key={field.name} options={field.options} value={values[field.name] || null} disabled={saving}
                                onChange={(_, value) => setValues(previous => ({ ...previous, [field.name]: value }))}
                                renderInput={params => <TextField {...params} label={field.label} required={Boolean(field.required)} />} />
                            : <TextField key={field.name} label={field.label} value={values[field.name] || ''} required={Boolean(field.required)} autoFocus disabled={saving}
                                onChange={event => setValues(previous => ({ ...previous, [field.name]: event.target.value }))} />)}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button disabled={saving} onClick={() => setCustomer(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={saving} sx={{ textTransform: 'none' }}>{saving ? 'Creating…' : 'Create Opportunity'}</Button>
                </DialogActions>
            </Box>
        </Dialog>
    </>;
};

export default CreateContactOpportunity;
